import { useEffect, useMemo, useState } from "react";
import { FiX, FiChevronDown } from "react-icons/fi";
import { LuSparkles } from "react-icons/lu";
import {
  ARTIFACT_SCOPES,
  ARTIFACT_SCOPE_LABELS,
} from "@kagami/shared/artifactScope";
import { SCOPE_ICONS } from "@/components/text-editor/artifactMentionItems";
import Button from "../ui/Button";
import Loader from "../loader";
import { Input } from "../ui/shadcn/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuCheckboxItem,
  DropdownMenuSeparator,
} from "../ui/shadcn/dropdown-menu";
import { apiFetch } from "@/lib/api";
import { useShow } from "@/contexts/ShowContext";
import { useUser } from "@/contexts/UserContext";

/**
 * Picker for the artifacts visible in this show — the current user's own (any
 * visibility), plus anyone else's public artifacts, plus the show's seeded
 * (ownerless) ones — whatever scope they were saved under, since filtering to
 * one scope would hide anything saved as a character, camera, lens, light or
 * style artifact. Resolves via onClose(artifact) with the chosen artifact, or
 * onClose(null) on cancel.
 *
 * Search (by name) and the scope filter both run client-side over the already-
 * fetched list — a show's artifacts are a small enough set that a second
 * round-trip per keystroke isn't worth it.
 */
export default function ArtifactLoaderModal({ onClose }) {
  const { show } = useShow();
  const { user } = useUser();
  const [artifacts, setArtifacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedScopes, setSelectedScopes] = useState(ARTIFACT_SCOPES);

  // Each artifact carries its own owner now (the list mixes creators) — "You"
  // for the current user's own, the owner's email for someone else's public
  // one, and a fixed label for a show-seeded artifact with no owner at all.
  const creatorLabel = (artifact) => {
    if (!artifact.user) return "Show default";
    if (artifact.user.id === user?.id) return "You";
    return artifact.user.email || "Unknown user";
  };

  const allScopesSelected = selectedScopes.length === ARTIFACT_SCOPES.length;

  const handleScopeChange = (scope, checked) => {
    setSelectedScopes((prev) =>
      checked
        ? Array.from(new Set([...prev, scope]))
        : prev.filter((s) => s !== scope),
    );
  };

  const filteredArtifacts = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return artifacts.filter((a) => {
      if (!selectedScopes.includes(a.scope)) return false;
      if (query && !(a.name || "").toLowerCase().includes(query)) return false;
      return true;
    });
  }, [artifacts, searchQuery, selectedScopes]);

  useEffect(() => {
    if (!show?._id) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    const run = async () => {
      try {
        const res = await apiFetch(`/api/shows/${show._id}/artifacts`);
        const data = res.ok ? await res.json() : [];
        if (!cancelled) setArtifacts(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Error loading artifacts:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [show?._id]);

  return (
    <div
      id="ArtifactLoaderModal"
      className="h-full w-full max-w-[600px] max-h-[720px] rounded-md flex flex-col bg-background border gap-4">
      <div className="px-4 pt-4 flex justify-between items-center">
        <h1 className="text-xl font-bold border-l-4 border-primary pl-4 capitalize">
          Artifacts
        </h1>
        <button
          type="button"
          onClick={() => onClose(null)}
          className="shrink-0 -mr-1 -mt-1 p-1.5 rounded-md text-secondary-foreground/70 hover:text-foreground hover:bg-secondary/60 cursor-pointer"
          title="Close">
          <FiX className="w-5 h-5" />
        </button>
      </div>

      <div className="flex items-center gap-2 px-4">
        <div className="relative flex-1">
          <Input
            id="ArtifactLoaderModal__search"
            type="text"
            placeholder="Search by name"
            className={` ${searchQuery ? "pr-7" : "pr-2"}`}
            onChange={(e) => setSearchQuery(e.target.value)}
            value={searchQuery}
          />
          {searchQuery && (
            <FiX
              className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-secondary-foreground/50 hover:text-secondary-foreground cursor-pointer"
              onClick={() => setSearchQuery("")}
            />
          )}
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="inline-flex h-9 shrink-0 outline-none items-center gap-2 px-2 text-xs rounded-md border bg-background hover:bg-secondary text-foreground cursor-pointer">
              <span>Scope</span>
              {!allScopesSelected ? (
                <span className="w-4 h-4 flex items-center justify-center text-[10px] bg-primary text-primary-foreground rounded-full leading-none">
                  {selectedScopes.length}
                </span>
              ) : (
                <FiChevronDown className="w-4 h-4 text-secondary-foreground/60" />
              )}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <button
              type="button"
              className="flex w-full items-center rounded-sm px-2 h-6 text-xs hover:bg-primary hover:text-primary-foreground cursor-pointer"
              onClick={() => setSelectedScopes(ARTIFACT_SCOPES)}>
              Select all
            </button>
            <DropdownMenuSeparator />
            {ARTIFACT_SCOPES.map((scope) => (
              <DropdownMenuCheckboxItem
                key={scope}
                checked={selectedScopes.includes(scope)}
                onCheckedChange={(checked) => handleScopeChange(scope, checked)}
                onSelect={(e) => e.preventDefault()}
                className="group text-xs h-6">
                <span className="text-sm shrink-0">
                  {SCOPE_ICONS[scope] || <LuSparkles />}
                </span>
                <span className="flex-1">{ARTIFACT_SCOPE_LABELS[scope]}</span>
                <button
                  type="button"
                  className="ml-auto hidden group-hover:inline-flex text-[10px] px-1.5 py-0.5 cursor-pointer rounded-sm text-primary-foreground hover:bg-primary-foreground hover:text-primary"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setSelectedScopes([scope]);
                  }}>
                  Only
                </button>
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="flex flex-col px-2 overflow-y-auto flex-1 relative">
        {!loading && filteredArtifacts.length === 0 && (
          <div className="py-8 text-center text-sm text-secondary-foreground/60">
            {artifacts.length === 0
              ? "No saved artifacts yet."
              : "No artifacts match your filters."}
          </div>
        )}
        {filteredArtifacts.map((a) => (
          <button
            id={`ArtifactLoaderModal__artifact-${a._id}`}
            key={a._id}
            onClick={() => onClose(a)}
            className="text-left px-3 py-2 rounded-sm hover:bg-secondary flex items-center gap-3 cursor-pointer">
            <span className="text-base shrink-0">
              {SCOPE_ICONS[a.scope] || <LuSparkles />}
            </span>
            <span className="flex flex-col min-w-0">
              <span className="text-sm font-medium truncate">
                {a.name || "Untitled artifact"}
              </span>
              <span className="text-[11px] text-secondary-foreground/60 truncate">
                {ARTIFACT_SCOPE_LABELS[a.scope] || a.scope} · {creatorLabel(a)}
              </span>
            </span>
          </button>
        ))}
        {loading && <Loader />}
      </div>

      <div className="flex justify-end p-2 border-t">
        <Button
          id="ArtifactLoaderModal__cancel"
          type="danger-ghost"
          className="min-w-20"
          onClick={() => onClose(null)}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
